import { staffClient } from "clients/staff";
import FileUploadingSpinner from "common/FileUploadingSpinner";
import BasicQuillEditor from "components/Form/Editor/Quill/Basic";
import Input from "components/Form/Input";
import MultiFileUploader from "components/Form/Input/File/MultiUploader";
import Select from "components/Form/Select";
import { useModal } from "components/Modal/Simple";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getS3UploadUrl, uploadS3File } from "utils/s3";
import PageContainer from "ui/PageContainer";
import PageHeader from "ui/PageHeader";
import Button from "ui/Button";
import Section from "ui/Section";
import { Switch } from "ui/shadcn";
import { Paperclip } from "lucide-react";


export const ArticleEditor = () => {
    const [files, setFiles] = useState([]);
    const [title, setTitle] = useState("");
    const [contents, setContents] = useState({});
    const [initialContents, setInitialContents] = useState({});
    const [board, setBoard] = useState(null);
    const [categoryItems, setCategoryItems] = useState(null);
    const [selectedCategoryItem, setSelectedCategoryItem] = useState(null);
    const [isPublic, setIsPublic] = useState(true);
    const [isImportant, setIsImportant] = useState(false);
    const { Modal, openModal, closeModal } = useModal();

    const params = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        updateData();
    }, []);

    const updateData = async () => {
        await updateBoard();
        if (params.articleId) {
            await updateArticle();
        }
    }

    console.log("[board]", board);
    const updateBoard = async () => {
        const ret = await staffClient.get(`/board/${params.boardId}`);
        console.log(ret);
        setBoard(ret.data);
        const tempCategoryItems = ret.data.categories.map((category) => ({
            name: category.name,
            data: category.id,
        }));
        tempCategoryItems.unshift({ name: "없음", data: null });
        setCategoryItems(tempCategoryItems);
        if (!_.isEmpty(tempCategoryItems)) {
            setSelectedCategoryItem(tempCategoryItems[0]);
        }
    };

    const updateArticle = async () => {
        const ret = await staffClient.get(`/article/${params.articleId}`);
        console.log("[updateAricle] ret", ret);
        setTitle(ret.data.title);
        setInitialContents(ret.data.contents);
        if (!_.isNil(ret.data.category)) {
            setSelectedCategoryItem({ name: ret.data.category.name, data: ret.data.category.id });
        }
        setFiles(ret.data.files);
        setIsPublic(ret.data.is_public ?? true);
        setIsImportant(ret.data.is_important ?? false);
    }

    const handleSave = async () => {
        console.log("[ArticleEditor] handleSave contents", contents);
        openModal();
        try {
            const uploadedFiles = [];
            for await (const file of files) {
                let uploadedUrl = null;
                if (file.url) {
                    uploadedFiles.push(file);
                    continue;
                }
                const upLoadUrl = await getS3UploadUrl(file);
                const ret = await uploadS3File(file, upLoadUrl);
                if (_.isNil(ret)) {
                    toast.error("파일 업로드에 실패했습니다.");
                    return;
                }
                uploadedUrl = ret;
                const uploadedFile = {
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    hash: file.hash,
                    url: uploadedUrl,
                }
                uploadedFiles.push(uploadedFile);
            }
            const body = {
                title,
                contents: contents,
                files: uploadedFiles,
                board_id: params.boardId,
                category_id: selectedCategoryItem.data,
                is_public: isPublic,
                is_important: isImportant,
            };
            console.log("[ArticleEditor] handleSave body", body);

            const from = searchParams.get('from');
            const siteId = searchParams.get('siteId');
            const queryString = from === 'site' && siteId ? `?from=site&siteId=${siteId}` : '';

            if (params.articleId) {
                const ret = await staffClient.patch(`/article/${params.articleId}`, body);
                console.log("[ArticleEditor] handleSave ret", ret);
                toast.success("게시글이 성공적으로 수정되었습니다.");
                navigate(`/boards/${params.boardId}/articles/${params.articleId}${queryString}`);
            } else {
                const ret = await staffClient.post(`/article`, body);
                console.log("[ArticleEditor] handleSave ret", ret);
                toast.success("게시글이 성공적으로 생성되었습니다.");
                if (from === 'site' && siteId) {
                    navigate(`/sites/${siteId}?tab=board`);
                } else {
                    navigate(-1);
                }
            }
        } catch (e) {
            console.log("[ArticleEditor] handleSave error", e);
            toast.error("게시글 저장에 실패했습니다.");
        } finally {
            closeModal();
        }
    }

    console.log("[ArticleEditor] params", params);
    console.log("[ArticleEditor] categoryItems", categoryItems);

    const from = searchParams.get('from');
    const siteId = searchParams.get('siteId');

    return (
        <PageContainer>
            <PageHeader
                title={params.articleId ? "게시글 수정" : "게시글 작성"}
                breadcrumb={
                    from === 'site' && siteId
                        ? [
                            { label: board?.name || '게시판', path: `/sites/${siteId}?tab=board` },
                            { label: params.articleId ? '수정' : '작성' }
                        ]
                        : [
                            { label: board?.name || '게시판', path: `/board/${params.boardId}` },
                            { label: params.articleId ? '수정' : '작성' }
                        ]
                }
                actions={
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={() => navigate(-1)}>
                            취소
                        </Button>
                        <Button variant="primary" onClick={handleSave}>
                            저장
                        </Button>
                    </div>
                }
            />

            <div className="space-y-4">
                {/* Board & Category Section */}
                <Section title="게시판 정보">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                                게시판
                            </label>
                            <Input
                                value={board?.name || ""}
                                readOnly={true}
                                disabled={true}
                            />
                        </div>
                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                                카테고리
                            </label>
                            <Select
                                items={categoryItems}
                                selectedItem={selectedCategoryItem}
                                onChange={setSelectedCategoryItem}
                            />
                        </div>
                    </div>
                </Section>

                {/* Title & Content Section */}
                <Section
                    title="게시글"
                    actions={
                        <div className="flex items-center gap-4">
                            {board?.board_type === 'notice' && (
                                <label className="flex cursor-pointer items-center gap-2">
                                    <span className={`text-sm font-medium ${isImportant ? 'text-warning' : 'text-muted-foreground'}`}>
                                        {isImportant ? '중요' : '일반'}
                                    </span>
                                    <Switch
                                        checked={isImportant}
                                        onCheckedChange={setIsImportant}
                                        className="data-[state=checked]:bg-warning"
                                        aria-label="중요 공지"
                                    />
                                </label>
                            )}
                            <label className="flex cursor-pointer items-center gap-2">
                                <span className={`inline-block w-12 text-right text-sm font-medium ${isPublic ? 'text-primary' : 'text-muted-foreground'}`}>
                                    {isPublic ? '공개' : '비공개'}
                                </span>
                                <Switch
                                    checked={isPublic}
                                    onCheckedChange={setIsPublic}
                                    aria-label="공개 여부"
                                />
                            </label>
                        </div>
                    }
                >
                    <div className="mb-4">
                        <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                            제목 <span className="text-destructive">*</span>
                        </label>
                        <Input
                            value={title}
                            onChange={setTitle}
                            placeholder="제목을 입력하세요"
                        />
                    </div>

                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                            내용 <span className="text-destructive">*</span>
                        </label>
                        <BasicQuillEditor
                            initialContents={initialContents}
                            onChange={setContents}
                            customStyles={{ minHeight: "400px" }}
                            imageUpload
                        />
                    </div>
                </Section>

                {/* Attachments Section */}
                <Section title="첨부파일" icon={<Paperclip />}>
                    <MultiFileUploader
                        files={files}
                        onFilesSelected={setFiles}
                    />
                </Section>
            </div>

            <Modal>
                <FileUploadingSpinner />
            </Modal>
        </PageContainer>
    );
};

export default ArticleEditor;
